import { DateTime } from "luxon";
import { parseRosterDate } from "./dates";

// Wording set by the client (task 114); do not paraphrase.
export const SAME_DATES_REQUIRED =
  "All travelers on a TripArmor® policy must have the same departure and return dates. Please remove travelers with different dates from this policy and purchase a separate policy for each set of travel dates.";

export interface TripDateIssue {
  row: number;
  message: string;
}

type DayInput = string | Date | null | undefined;

const toDay = (value: DayInput): string | null => {
  if (!value) return null;
  const day = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(String(value));
  return day.isValid ? day.toISODate() : null;
};

const pretty = (key: string) =>
  key
    .split("|")
    .map((day) => DateTime.fromISO(day).toFormat("MMM d, yyyy"))
    .join(" – ");

// IMG takes one set of dates for the whole policy, the one chosen on the
// quote's first step; any other date on a row was silently dropped. Every row
// that carries dates must agree with the others and with the policy.
//
// Rows that disagree with most of the roster are the ones flagged, so the
// person sees which travelers to move to a separate policy. A roster that
// agrees with itself but not with the quote is flagged throughout.
export const tripDateIssues = (
  rows: { start?: string | null; end?: string | null }[],
  policy: { start: DayInput; end: DayInput }
): TripDateIssue[] => {
  const keys = rows.map(({ start, end }) => {
    const from = parseRosterDate(start);
    const to = parseRosterDate(end);
    return from && to ? `${from.toISODate()}|${to.toISODate()}` : null;
  });

  const counts = new Map<string, number>();
  keys.forEach((key) => key && counts.set(key, (counts.get(key) ?? 0) + 1));
  if (counts.size === 0) return [];

  if (counts.size > 1) {
    // Most common dates win; a tie goes to whichever comes first in the file.
    let common = keys.find(Boolean) as string;
    counts.forEach((count, key) => {
      if (count > (counts.get(common) ?? 0)) common = key;
    });

    return keys.flatMap((key, row) =>
      key && key !== common ? [{ row, message: SAME_DATES_REQUIRED }] : []
    );
  }

  const [only] = Array.from(counts.keys());
  const policyStart = toDay(policy.start);
  const policyEnd = toDay(policy.end);
  if (!policyStart || !policyEnd || only === `${policyStart}|${policyEnd}`) return [];

  const message = `These travel dates (${pretty(only)}) do not match the dates of this quote (${pretty(
    `${policyStart}|${policyEnd}`
  )}). Change the dates on the first step of the quote, or correct them in your file.`;

  return keys.flatMap((key, row) => (key ? [{ row, message }] : []));
};
